import { Database } from "@/integrations/supabase/types";

type ReportDownload = Database["public"]["Tables"]["report_downloads"]["Row"];

/**
 * Escapes a CSV field value to handle commas, quotes, and newlines
 */
function escapeCSVField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    // Escape quotes by doubling them and wrap in quotes
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Formats a date string to a readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Formats consent as Yes/No
 */
function formatConsent(consent: boolean | null): string {
  return consent ? "Yes" : "No";
}

/**
 * Formats screen resolution for display
 */
function formatScreenResolution(width: number | null, height: number | null): string {
  if (width && height) {
    return `${width}x${height}`;
  }
  return "-";
}

/**
 * Converts report downloads to CSV format
 */
export function exportReportDownloadsToCSV(downloads: ReportDownload[]): string {
  // CSV Headers
  const headers = [
    "ID",
    "Name",
    "Email",
    "LinkedIn URL",
    "Report Version",
    "Marketing Consent",
    "Download Date",
    "Source",
    "Browser",
    "OS",
    "Device Type",
    "Device",
    "Screen Resolution",
    "IP Address",
  ];

  // Create header row
  const headerRow = headers.map(escapeCSVField).join(",");

  // Create data rows
  const dataRows = downloads.map((download) => {
    const id = download.id.slice(0, 8); // Short ID
    const name = download.name || "-";
    const email = download.email || "-";
    const linkedinUrl = download.linkedin_url || "-";
    const reportVersion = download.report_version_label || "-";
    const marketingConsent = formatConsent(download.marketing_consent);
    const date = formatDate(download.created_at);
    const source = download.source || "homepage_banner";

    // Browser info
    const browser = download.browser_name
      ? `${download.browser_name}${download.browser_version ? ` ${download.browser_version}` : ""}`
      : "-";

    // OS info
    const os = download.os_name
      ? `${download.os_name}${download.os_version ? ` ${download.os_version}` : ""}`
      : "-";

    // Device type
    const deviceType = download.device_type || "-";

    // Device brand/model
    const device = download.device_brand || download.device_model
      ? `${download.device_brand || ""}${download.device_model ? ` ${download.device_model}` : ""}`.trim()
      : "-";

    // Screen resolution
    const screenResolution = formatScreenResolution(download.screen_width, download.screen_height);

    // IP Address
    const ipAddress = download.ip_address || "-";

    return [
      escapeCSVField(id),
      escapeCSVField(name),
      escapeCSVField(email),
      escapeCSVField(linkedinUrl),
      escapeCSVField(reportVersion),
      escapeCSVField(marketingConsent),
      escapeCSVField(date),
      escapeCSVField(source),
      escapeCSVField(browser),
      escapeCSVField(os),
      escapeCSVField(deviceType),
      escapeCSVField(device),
      escapeCSVField(screenResolution),
      escapeCSVField(ipAddress),
    ].join(",");
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join("\n");
}

/**
 * Triggers a browser download of the CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
